import { BtnPrimary } from '../ui/components';
import { api } from '../api/client';
import { state } from '../store';
import { t } from '../i18n';

// ─── Selected plan tracking ─────────────────────────────────────────

let selectedPremiumPlan = 'premium_12m';  // default = "BEST DEAL" option
let selectedCoinPlan = 'coins_500';       // default = "VALUE" option

// ─── Window declarations ────────────────────────────────────────────

declare global {
  interface Window {
    selectPremiumOption: (el: HTMLElement) => void;
    selectCoinOption: (el: HTMLElement) => void;
    handlePremiumPurchase: () => void;
    handleCoinsPurchase: () => void;
  }
}

// ─── Selection handlers ─────────────────────────────────────────────

window.selectPremiumOption = (element: HTMLElement) => {
  // Remove selected classes from all
  document.querySelectorAll('.price-option-card').forEach(el => {
    el.classList.remove('border-neon-gold', 'bg-neon-gold/10', 'shadow-[0_0_20px_rgba(255,215,0,0.2)]');
    el.classList.add('border-white/[0.08]');
  });
  // Add selected classes to clicked
  element.classList.remove('border-white/[0.08]');
  element.classList.add('border-neon-gold', 'bg-neon-gold/10', 'shadow-[0_0_20px_rgba(255,215,0,0.2)]');

  // Save selected plan
  selectedPremiumPlan = element.dataset.plan || selectedPremiumPlan;
};

window.selectCoinOption = (element: HTMLElement) => {
  document.querySelectorAll('.coin-option-card').forEach(el => {
    el.classList.remove('border-neon-gold', 'bg-neon-gold/10', 'shadow-[0_0_20px_rgba(255,215,0,0.2)]');
    el.classList.add('border-[#ffd700]/[0.15]');
  });
  element.classList.remove('border-[#ffd700]/[0.15]');
  element.classList.add('border-neon-gold', 'bg-neon-gold/10', 'shadow-[0_0_20px_rgba(255,215,0,0.2)]');

  // Save selected plan
  selectedCoinPlan = element.dataset.plan || selectedCoinPlan;
};

// ─── Purchase handlers ──────────────────────────────────────────────

let _premiumPurchaseInProgress = false;
let _coinsPurchaseInProgress = false;


window.handlePremiumPurchase = async () => {
  if (!selectedPremiumPlan || _premiumPurchaseInProgress) return;
  _premiumPurchaseInProgress = true;
  const btn = document.querySelector('[data-action="premium-purchase"]') as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.style.opacity = '0.7'; const icon = btn.querySelector('.icon'); if (icon) icon.textContent = '⏳'; }
  try {
    const res = await Promise.race([
      api.createPurchase(selectedPremiumPlan),
      new Promise<never>((_, rej) => setTimeout(() => rej(new Error('timeout')), 10000)),
    ]);
    (window as any).Telegram?.WebApp?.openInvoice((res as any).invoice_link, async (status: string) => {
      if (status === 'paid') {
        const freshUser = await api.getUser();
        state.user = freshUser;
        await (window as any).__reinit?.();
        state.games = [];
        state.levels = {};
        window.showToast('👑 Premium активирован!', 'success');
        window.navTo('home');
        window.renderApp();
      }
    });
  } catch (e) {
    console.error('Premium purchase failed:', e);
    window.showToast(t('error_generic'), 'error');
  } finally {
    _premiumPurchaseInProgress = false;
    if (btn) { btn.disabled = false; btn.style.opacity = ''; const icon = btn.querySelector('.icon'); if (icon) icon.textContent = '👑'; }
  }
};

window.handleCoinsPurchase = async () => {
  if (!selectedCoinPlan || _coinsPurchaseInProgress) return;
  _coinsPurchaseInProgress = true;
  const btn = document.querySelector('[data-action="coins-purchase"]') as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.style.opacity = '0.7'; const icon = btn.querySelector('.icon'); if (icon) icon.textContent = '⏳'; }
  try {
    const res = await Promise.race([
      api.createPurchase(selectedCoinPlan),
      new Promise<never>((_, rej) => setTimeout(() => rej(new Error('timeout')), 10000)),
    ]);
    (window as any).Telegram?.WebApp?.openInvoice((res as any).invoice_link, async (status: string) => {
      if (status === 'paid') {
        state.user = await api.getUser();
        window.showToast(t('toast_coins'), 'success');
        window.renderApp();
      }
    });
  } catch (e) {
    console.error('Coins purchase failed:', e);
    window.showToast(t('error_generic'), 'error');
  } finally {
    _coinsPurchaseInProgress = false;
    if (btn) { btn.disabled = false; btn.style.opacity = ''; const icon = btn.querySelector('.icon'); if (icon) icon.textContent = '🪙'; }
  }
};

// ─── Render ─────────────────────────────────────────────────────────

export function renderPremium(): string {
  return `
    <div id="screen-premium" class="absolute inset-0 overflow-y-auto overflow-x-hidden pb-[96px] z-10 scrollbar-none bg-void text-t-primary">
      <div class="pt-[16px] px-[20px] pb-0 flex justify-end">
        <div class="w-[38px] h-[38px] bg-white/[0.07] border border-b-glass rounded-full flex items-center justify-center cursor-pointer text-[18px] transition-all active:scale-[0.92] active:bg-white/[0.12]" data-action="nav" data-to="home">✕</div>
      </div>

      <div class="text-center pt-[10px] px-[20px] pb-[10px] flex items-center justify-center gap-[8px]">
        <div class="text-[32px] animate-[iconPulse_2.5s_ease-in-out_infinite]">👑</div>
        <div class="font-display text-[32px] font-extrabold bg-grad-premium bg-clip-text text-transparent">${t('premium_title')}</div>
      </div>
      <div class="text-center px-[20px] pb-[16px]">
        <div class="text-[14px] text-t-secondary font-semibold">${t('premium_sub')}</div>
      </div>

      <div class="flex flex-col gap-[10px] px-[16px]">
        <div class="flex items-center gap-[14px] p-[12px_16px] bg-[#ffd700]/[0.06] border border-[#ffd700]/[0.15] rounded-[18px]">
          <div class="text-[26px] shrink-0">🔓</div>
          <div>
            <div class="text-[15px] font-extrabold text-t-primary">${t('premium_f1_title')}</div>
            <div class="text-[12px] text-t-secondary mt-[2px] font-semibold">${t('premium_f1_sub')}</div>
          </div>
        </div>
        <div class="flex items-center gap-[14px] p-[12px_16px] bg-[#ffd700]/[0.06] border border-[#ffd700]/[0.15] rounded-[18px]">
          <div class="text-[26px] shrink-0">✨</div>
          <div>
            <div class="text-[15px] font-extrabold text-t-primary">${t('premium_f2_title')}</div>
            <div class="text-[12px] text-t-secondary mt-[2px] font-semibold">${t('premium_f2_sub')}</div>
          </div>
        </div>
        <div class="flex items-center gap-[14px] p-[12px_16px] bg-[#ffd700]/[0.06] border border-[#ffd700]/[0.15] rounded-[18px]">
          <div class="text-[26px] shrink-0">🚀</div>
          <div>
            <div class="text-[15px] font-extrabold text-t-primary">${t('premium_f3_title')}</div>
            <div class="text-[12px] text-t-secondary mt-[2px] font-semibold">${t('premium_f3_sub')}</div>
          </div>
        </div>
      </div>

      <div class="text-[12px] font-extrabold tracking-[1.5px] uppercase text-t-muted mb-[12px] px-[20px] mt-[24px]">${t('premium_choose_plan')}</div>
      <div class="grid grid-cols-2 gap-[10px] px-[16px]">
        <div class="price-option-card border-white/[0.08] border-[1.5px] rounded-[18px] p-[16px_10px] text-center cursor-pointer transition-all relative" data-plan="premium_1m" data-action="select-premium-option">
          <div class="text-[15px] font-extrabold text-white uppercase">${t('premium_month1')}</div>
          <div class="font-display text-[18px] font-extrabold text-t-secondary my-[4px]">⭐️ 150</div>
          <div class="text-[10px] text-t-muted font-semibold mt-[2px]">${t('premium_per_day').replace('{n}', '5')}</div>
        </div>
        <div class="price-option-card border-white/[0.08] border-[1.5px] rounded-[18px] p-[16px_10px] text-center cursor-pointer transition-all relative" data-plan="premium_3m" data-action="select-premium-option">
          <div class="absolute -top-[10px] left-1/2 -translate-x-1/2 bg-grad-gold text-[#1a0a00] text-[9px] font-extrabold tracking-[0.5px] px-[8px] py-[3px] rounded-pill whitespace-nowrap">${t('premium_badge_popular')}</div>
          <div class="text-[15px] font-extrabold text-white uppercase">${t('premium_month3')}</div>
          <div class="font-display text-[18px] font-extrabold text-t-secondary my-[2px]">⭐️ 350</div>
          <div class="text-[10px] text-t-muted font-semibold mt-[2px]">${t('premium_per_day').replace('{n}', '4')}</div>
        </div>
        <div class="price-option-card border-neon-gold bg-neon-gold/10 shadow-[0_0_20px_rgba(255,215,0,0.2)] border-[1.5px] rounded-[18px] p-[16px_10px] text-center cursor-pointer transition-all relative col-span-2" data-plan="premium_12m" data-action="select-premium-option">
          <div class="absolute -top-[10px] left-1/2 -translate-x-1/2 bg-grad-gold text-[#1a0a00] text-[9px] font-extrabold tracking-[0.5px] px-[8px] py-[3px] rounded-pill whitespace-nowrap">${t('premium_badge_best_deal')}</div>
          <div class="text-[15px] font-extrabold text-white uppercase">${t('premium_month12')}</div>
          <div class="flex items-center justify-center gap-[8px] my-[2px]">
            <div class="font-display text-[13px] font-extrabold text-t-muted line-through">⭐️ 1500</div>
            <div class="font-display text-[18px] font-extrabold text-white">⭐️ 999</div>
          </div>
          <div class="text-[10px] text-neon-green font-bold mt-[2px]">−33% · ${t('premium_per_day').replace('{n}', '3')}</div>
        </div>
      </div>

      <div class="px-[16px] pt-[16px] pb-[4px]">
        ${BtnPrimary({ text: t('premium_get_btn'), icon: '👑', className: '!bg-grad-gold shadow-glow-gold !text-[#1a1100]', onClick: 'premium-purchase' })}
        <div class="text-center mt-[10px] text-[12px] text-t-muted font-semibold">
          ${t('premium_cancel')}
        </div>
        <div class="text-center mt-[4px] text-[12px] text-neon-pink cursor-pointer font-bold" data-action="nav" data-to="invite">
          ${t('premium_invite_cta')}
        </div>
      </div>

      <div class="h-[1px] bg-white/[0.06] my-[20px] mx-[16px]"></div>

      <div class="text-[12px] font-extrabold tracking-[1.5px] uppercase text-t-muted mb-[12px] px-[20px] mt-[16px]">${t('premium_buy_coins')}</div>
      <div class="px-[16px] pb-[8px] text-[13px] text-t-secondary font-semibold">
        ${t('premium_coins_sub')}
      </div>
      <div class="grid grid-cols-3 gap-[8px] px-[16px]">
        <div class="coin-option-card rounded-[18px] p-[14px_4px] text-center bg-[#ffd700]/[0.06] border-[1.5px] border-[#ffd700]/[0.15] cursor-pointer transition-all active:scale-[0.96] active:border-neon-gold active:bg-neon-gold/10" data-plan="coins_100" data-action="select-coin-option">
          <div class="text-[10px] text-t-secondary font-bold uppercase mb-[4px]">${t('coin_starter')}</div>
          <div class="font-display text-[16px] font-extrabold text-neon-gold">🪙 100</div>
          <div class="text-[14px] text-white my-[2px] font-bold mt-[6px]">⭐️ 40</div>
        </div>
        <div class="coin-option-card rounded-[18px] p-[14px_4px] text-center bg-[#ffd700]/[0.1] border-[1.5px] border-neon-gold shadow-[0_0_20px_rgba(255,215,0,0.2)] cursor-pointer transition-all active:scale-[0.96] active:border-neon-gold active:bg-neon-gold/10 relative" data-plan="coins_500" data-action="select-coin-option">
          <div class="absolute -top-[10px] left-1/2 -translate-x-1/2 bg-grad-gold text-[#1a0a00] text-[8px] font-extrabold whitespace-nowrap px-[8px] py-[3px] rounded-pill">${t('premium_badge_value')}</div>
          <div class="text-[10px] text-neon-gold font-bold uppercase mb-[4px]">${t('coin_hit')}</div>
          <div class="font-display text-[16px] font-extrabold text-neon-gold">🪙 500</div>
          <div class="text-[14px] text-white my-[2px] font-bold mt-[6px]">⭐️ 140</div>
        </div>
        <div class="coin-option-card rounded-[18px] p-[14px_4px] text-center bg-[#ffd700]/[0.06] border-[1.5px] border-[#ffd700]/[0.15] cursor-pointer transition-all active:scale-[0.96] active:border-neon-gold active:bg-neon-gold/10 relative" data-plan="coins_1500" data-action="select-coin-option">
          <div class="absolute -top-[10px] left-1/2 -translate-x-1/2 bg-grad-gold text-[#1a0a00] text-[8px] font-extrabold whitespace-nowrap px-[8px] py-[3px] rounded-pill">${t('premium_badge_best_deal')}</div>
          <div class="text-[10px] text-t-secondary font-bold uppercase mb-[4px]">${t('coin_mega')}</div>
          <div class="font-display text-[16px] font-extrabold text-neon-gold">🪙 1500</div>
          <div class="text-[14px] text-white my-[2px] font-bold mt-[6px]">⭐️ 350</div>
        </div>
      </div>

      <div class="px-[16px] pt-[16px]">
        ${BtnPrimary({ text: t('premium_buy_btn'), icon: '🪙', className: '!bg-grad-gold shadow-glow-gold !text-[#1a1100]', onClick: 'coins-purchase' })}
      </div>

      <div class="h-[24px]"></div>
    </div>
  `;
}
